import { useEffect, useState } from "react";
import type {
  FreightRate, FreightType, FreightMode, FreightBasis, FreightCurrency,
} from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { ReadOnlySave } from "@/components/ReadOnlySave";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

const ORIGINS = ["US Factory", "China", "Goa Plant", "Local Stock"];
const DESTINATIONS = ["Bangalore Warehouse", "Chennai Warehouse"];
const MODES: FreightMode[] = ["Sea", "Air", "Road"];
const BASES: FreightBasis[] = ["per CBM", "per kg", "per container", "per trip"];
const CURRS: FreightCurrency[] = ["USD", "INR"];

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={LABEL}>{children}</span>;
}

function TextField({
  label, testid, value, onChange, placeholder,
}: { label: string; testid: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}
function NumberField({
  label, testid, value, onChange, allowNull = false,
}: { label: string; testid: string; value: number | null; onChange: (v: number | null) => void; allowNull?: boolean }) {
  const [buf, setBuf] = useState<string>(value == null ? "" : String(value));
  useEffect(() => { setBuf(value == null ? "" : String(value)); }, [value]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}{allowNull && <span style={{ color: semanticColors.textPlaceholder, textTransform: "none", marginLeft: spacing[6] }}>(optional)</span>}</FieldLabel>
      <input data-testid={testid} type="number" value={buf}
             onChange={(e) => {
               setBuf(e.target.value);
               if (e.target.value === "" && allowNull) onChange(null);
               else {
                 const n = parseFloat(e.target.value);
                 if (Number.isFinite(n)) onChange(n);
               }
             }}
             style={{ ...INPUT, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
    </div>
  );
}
function SelectField<T extends string>({
  label, testid, value, options, onChange,
}: { label: string; testid: string; value: T; options: readonly T[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value as T)} style={INPUT}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function DateField({
  label, testid, value, onChange, allowEmpty = false,
}: { label: string; testid: string; value: string | null; onChange: (v: string | null) => void; allowEmpty?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}{allowEmpty && <span style={{ color: semanticColors.textPlaceholder, textTransform: "none", marginLeft: spacing[6] }}>(optional)</span>}</FieldLabel>
      <input data-testid={testid} type="date" value={value ?? ""}
             onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} style={INPUT} />
    </div>
  );
}
function TypeRadio({ value, onChange }: { value: FreightType; onChange: (v: FreightType) => void }) {
  const opts: FreightType[] = ["International", "Domestic"];
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>Type</FieldLabel>
      <div className="flex items-center" style={{ gap: spacing[16], height: heights.input }}>
        {opts.map((o) => (
          <label key={o} data-testid={`radio-type-${o.toLowerCase()}`}
                 className="flex items-center"
                 style={{ gap: spacing[6], fontSize: typography.fontSize.base, color: semanticColors.textPrimary, cursor: "pointer" }}>
            <input type="radio" name="freightType" checked={value === o}
                   onChange={() => onChange(o)} style={{ accentColor: colors.indigo600 }} />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
}
function ActiveToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <FieldLabel>Active</FieldLabel>
      {/* No transition here, unlike the other two detail panels' toggles —
          that's how this one already was, kept as-is (zero visual change). */}
      <button data-testid="freight-active-toggle" onClick={() => onChange(!value)}
              style={{
                width: layout.toggleTrackWidth, height: heights.pill, padding: spacing[2], borderRadius: radius.full,
                border: "none", cursor: "pointer",
                backgroundColor: value ? colors.indigo600 : semanticColors.textDisabled,
                display: "flex", alignItems: "center",
                justifyContent: value ? "flex-end" : "flex-start",
              }}>
        <span style={{ width: heights.icon, height: heights.icon, borderRadius: "50%",
                       backgroundColor: colors.white, boxShadow: shadows.toggleKnob }} />
      </button>
    </div>
  );
}

export function FreightRateDetailPanel({
  rate, onClose,
}: {
  rate: FreightRate | null;
  onClose: () => void;
}) {
  const [form, setForm] = useState<FreightRate | null>(rate);
  useEffect(() => { setForm(rate); }, [rate]);
  if (!rate || !form) return null;

  const set = <K extends keyof FreightRate>(k: K, v: FreightRate[K]) =>
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));

  return (
    <DetailPanel
      testIdPrefix="freight"
      title={`${form.origin} → ${form.destination}`}
      subtitle={form.id}
      onClose={onClose}
      footer={
        <>
          <button data-testid="freight-panel-cancel" className="btn-secondary" onClick={onClose}
                  style={{ height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
                           color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
                           borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer" }}>
            Cancel
          </button>
          <ReadOnlySave testId="freight-panel-save" />
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <SelectField label="Origin" testid="ff-origin" value={form.origin}
                       options={ORIGINS} onChange={(v) => set("origin", v)} />
          <SelectField label="Destination" testid="ff-destination" value={form.destination}
                       options={DESTINATIONS} onChange={(v) => set("destination", v)} />
        </div>

        <TypeRadio value={form.type} onChange={(v) => set("type", v)} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <SelectField label="Mode" testid="ff-mode" value={form.mode}
                       options={MODES} onChange={(v) => set("mode", v)} />
          <SelectField label="Basis" testid="ff-basis" value={form.basis}
                       options={BASES} onChange={(v) => set("basis", v)} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <NumberField label="Rate" testid="ff-rate" value={form.rate}
                       onChange={(v) => set("rate", v ?? 0)} />
          <SelectField label="Currency" testid="ff-currency" value={form.currency}
                       options={CURRS} onChange={(v) => set("currency", v)} />
          <NumberField label="Min Charge" testid="ff-min-charge" value={form.minCharge}
                       onChange={(v) => set("minCharge", v)} allowNull />
          <NumberField label="Transit Days" testid="ff-transit-days" value={form.transitDays}
                       onChange={(v) => set("transitDays", Math.max(0, Math.floor(v ?? 0)))} />
        </div>

        <TextField label="Carrier" testid="ff-carrier" value={form.carrier}
                   onChange={(v) => set("carrier", v)} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <DateField label="Valid From" testid="ff-valid-from" value={form.validFrom}
                     onChange={(v) => set("validFrom", v ?? "")} />
          <DateField label="Valid To" testid="ff-valid-to" value={form.validTo}
                     onChange={(v) => set("validTo", v)} allowEmpty />
        </div>

        <TextField label="Notes" testid="ff-notes" value={form.notes}
                   onChange={(v) => set("notes", v)}
                   placeholder="e.g. excludes port handling charges" />

        <ActiveToggle value={form.active} onChange={(v) => set("active", v)} />
      </div>
    </DetailPanel>
  );
}
